import type { FC } from 'react';
import { useMemo } from 'react';

import { t } from '@luna/i18n';

import { useEmails } from '../useEmails';
import { convertEmailsToSelectItems, getSelectEmails } from './EmailSelectService';
import { isEmailEntryAllowed } from '../EmailsService';
import type { Email } from '../ContactTypes';
import { ContactSelect } from './ContactSelect';
import { useContactListFlags } from '../useContacts';
import type { Contact } from '../ContactDataManagementTypes';

export type EmailSelectProps = {
    'data-cy'?: string;
    name?: string;
    placeholder?: string;
    disabled?: boolean;
    addValueToItemsIfNotIncluded?: boolean;
    preselectPrimaryEmail?: boolean;
    emailId: string | undefined;
    onSelect: (emails: Email[]) => void;
    filterContacts?: (contacts: Contact[]) => Contact[];
};

type Props = EmailSelectProps & {
    addNewEmailHref?: string;
    onAddNewEmailClick?(): void;
};

export const SharedEmailSelect: FC<Props> = ({
    'data-cy': dataCy,
    name,
    placeholder,
    disabled,
    emailId,
    addNewEmailHref,
    onAddNewEmailClick,
    addValueToItemsIfNotIncluded,
    preselectPrimaryEmail,
    onSelect,
    filterContacts,
}) => {
    const { flags } = useContactListFlags();
    const [{ emails, isLoading, hasError }, fetchContacts] = useEmails(filterContacts);

    const allEmails = useMemo<Email[]>(
        () => getSelectEmails(emails, emailId, addValueToItemsIfNotIncluded),
        [emailId, emails, addValueToItemsIfNotIncluded],
    );

    return (
        <ContactSelect
            data-cy={dataCy}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            value={emailId}
            onSelect={onSelect}
            contacts={allEmails}
            isLoading={isLoading}
            hasError={hasError}
            isNewContactAllowed={isEmailEntryAllowed(flags)}
            invalidContactText={t('contactDataManagement.selectedEmailIsNotValidWarning')}
            invalidContactBtnLabel={t('contactDataManagement.manageEmailsLink')}
            preselectPrimaryContact={preselectPrimaryEmail}
            addNewContactHref={addNewEmailHref}
            onAddNeContactClick={onAddNewEmailClick}
            convertContactsToSelectItems={convertEmailsToSelectItems}
            fetchContacts={fetchContacts}
        />
    );
};
